// „Bearbeiten (eigene Kopie)", „Frei gestalten" und Baustein „Vorlage" im Studio (Erweiterung zu M16/M17):
// Knopf für schreibgeschützte Vorlagen, Aufteilen der Folie (Vorlage + Bereich) und Eigenschaften einer eingebetteten Vorlage.
import { createElement, fillTargets, type LayoutElement, type LayoutV1, type LayoutV2 } from '@disploraq/layouts';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { disabledWidgets } from '../../api/features';
import { can, useLayouts, useMe } from '../../api/queries';
import { studioPath, useEditCopy } from '../../api/template-edit';
import type { LayoutRow } from '../../api/types';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/toast';
import type { MediaIndex } from '../../components/Preview';

/**
 * „Bearbeiten (eigene Kopie)": legt eine eigene Kopie an (Zonenvorlagen werden frei gestaltbar) und öffnet sie im Studio.
 * onCopied ersetzt das Öffnen (z. B. Playlist: Kopie erst als Folie anhängen).
 */
export function EditCopyButton({ layout, back, small, plain, onCopied }: {
  layout: Pick<LayoutRow, 'id' | 'name'>; back?: string; small?: boolean; plain?: boolean; onCopied?: (copy: LayoutRow) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const editCopy = useEditCopy();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (!can(me, 'layouts.manage')) return null;
  const run = async () => {
    setBusy(true);
    try {
      const res = await editCopy(layout.id);
      toast.show(t('tedit.copied', { name: res.layout.name }));
      if (onCopied) await onCopied(res.layout);
      else navigate(studioPath(res.layout.id, back));
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className={`btn${small ? ' sm' : ''}${plain ? '' : ' primary'}`} disabled={busy} data-testid="edit-copy"
      title={t('tedit.editCopyTitle')} onClick={(e) => { e.stopPropagation(); void run(); }}>
      <Icon name="edit" />{t('tedit.editCopy')}
    </button>
  );
}

/** Hinweis im Studio statt des passiven „schreibgeschützt": erklärt und bietet die Kopie an. */
export function ReadonlyPanel({ layout, back }: { layout: LayoutRow; back?: string }) {
  const { t } = useTranslation();
  return (
    <div className="hint col" style={{ gap: 8 }} data-testid="readonly-panel">
      <b>{t('tedit.readonlyTitle')}</b>
      <span className="small">{t('tedit.readonlyText')}</span>
      <EditCopyButton layout={layout} back={back} />
    </div>
  );
}

type El = LayoutElement & { id: string };
export type SplitKind = 'side' | 'bottom';
const uid = () => Math.random().toString(36).slice(2, 10);
const r1 = (v: number) => Math.round(v * 10) / 10;

/**
 * Folie aufteilen: Vorhandenes wird in den Hauptbereich verkleinert (leere Folie: Baustein „Vorlage"), daneben bzw.
 * darunter entsteht ein neuer Bereich (Text-Fläche bzw. Laufschrift). Liefert die Elemente und die id des neuen Bereichs.
 */
export function splitElements(elements: El[], kind: SplitKind, areaText: string): { elements: El[]; added: string } {
  const main = kind === 'side' ? { x: 0, y: 0, w: 70, h: 100 } : { x: 0, y: 0, w: 100, h: 88 };
  const fx = main.w / 100;
  const fy = main.h / 100;
  const squeezed: El[] = elements.length
    ? elements.map((e) => ({ ...e, x: r1(e.x * fx), y: r1(e.y * fy), w: r1(e.w * fx), h: r1(e.h * fy) }))
    : [{ ...createElement('layout', main.x, main.y, main.w, main.h), id: uid() } as El];
  const area = (kind === 'side'
    ? { ...createElement('text', 70, 0, 30, 100, { text: areaText, size: 4, bg: '#1b1b1b', align: 'center' }), id: uid() }
    : { ...createElement('ticker', 0, 88, 100, 12), id: uid() }) as El;
  return { elements: [...squeezed, area], added: area.id };
}

/** Kleine Leiste „Aufteilen" im Bausteine-Bereich. */
export function SplitButtons({ onSplit }: { onSplit: (k: SplitKind) => void }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="lbl" style={{ marginTop: 8 }}>{t('tedit.split')}</div>
      <div className="muted small tip" style={{ padding: '0 8px' }}>{t('tedit.splitHint')}</div>
      <button onClick={() => onSplit('side')} data-testid="split-side"><Icon name="layout" />{t('tedit.splitSide')}</button>
      <button onClick={() => onSplit('bottom')} data-testid="split-bottom"><Icon name="ticker" />{t('tedit.splitBottom')}</button>
    </>
  );
}

type Embed = Extract<LayoutElement, { type: 'layout' }>;
type EmbedContent = NonNullable<Embed['content']>;

/** Eigenschaften des Bausteins „Vorlage": Vorlage wählen, Anpassen, Felder der eingebetteten Vorlage füllen. */
export function EmbedProps({ el, selfId, media, disabled, onPatch }: {
  el: Embed; selfId: string; media: MediaIndex; disabled: boolean; onPatch: (p: Partial<Embed>) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'de';
  const { data: me } = useMe();
  const { data: layouts = [] } = useLayouts();
  // Nur veröffentlichte Vorlagen (Bildschirme zeigen deren letzte Revision), nie die eigene; nicht freigeschaltete
  // Bausteine darin wie im Studio nicht neu einsetzbar (globale Vorlagen filtert bereits der Server)
  const options = layouts.filter((l) => l.id === el.layoutId
    || (l.id !== selfId && l.latestRevisionNo !== null && !disabledWidgets(me, l.definition).length));
  const chosen = layouts.find((l) => l.id === el.layoutId);
  const def = chosen?.definition as LayoutV1 | LayoutV2 | undefined;
  const fields = def?.fields ?? [];
  const targets = def ? new Map(fillTargets(def).map((x) => [x.id, x])) : new Map();
  const content: EmbedContent = el.content ?? {};
  const setField = (zone: string, v: EmbedContent[string] | null) => {
    const n = { ...content };
    if (v === null) delete n[zone];
    else n[zone] = v;
    onPatch({ content: n });
  };
  const images = media.list.filter((m) => m.kind === 'image' && m.current?.status === 'ready');
  const elText = (id: string): string => {
    const e = def?.specVersion === 2 ? def.elements.find((x) => x.id === id) : undefined;
    return e && 'text' in e ? (e.text ?? '') : '';
  };
  return (
    <>
      <label className="field">{t('tedit.embedPick')}
        <select disabled={disabled} value={el.layoutId ?? ''} data-testid="embed-pick"
          onChange={(e) => { const l = layouts.find((x) => x.id === e.target.value); onPatch({ layoutId: e.target.value, content: {}, ...(l ? { orientation: l.orientation } : {}) }); }}>
          <option value="">{t('tedit.embedNone')}</option>
          {options.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.orientation === 'portrait' ? '9:16' : '16:9'}{l.scope === 'global' ? ` · ${t('templates.global')}` : ''}</option>)}
        </select>
        <span className="small muted">{t('tedit.embedHint')}</span>
      </label>
      <label className="field">{t('studio.f.fit')}
        <select disabled={disabled} value={el.fit ?? 'contain'} onChange={(e) => onPatch({ fit: e.target.value as Embed['fit'] })}>
          {(['contain', 'cover', 'fill'] as const).map((o) => <option key={o} value={o}>{t(`studio.o.fit.${o}`)}</option>)}
        </select>
      </label>
      {chosen && (
        <div className="field">{t('tedit.embedFields')}
          {!fields.length && <span className="small muted">{t('tedit.embedNoFields')}</span>}
          {fields.map((f) => {
            const kind = targets.get(f.zone)?.kind;
            const c = content[f.zone];
            const label = f.label[lang];
            if (kind === 'text' || kind === 'rows') {
              return (
                <label key={f.zone} className="field small">{label}
                  <textarea rows={kind === 'rows' ? 3 : 2} disabled={disabled} value={c?.type === 'text' ? c.value : ''} placeholder={elText(f.zone)}
                    onChange={(e) => setField(f.zone, e.target.value ? { type: 'text', value: e.target.value } : null)} />
                </label>
              );
            }
            if (kind === 'media' || kind === 'logo' || kind === 'playlist') {
              const v = c?.type === 'media' ? c.asset : c?.type === 'playlist' ? (c.items[0]?.asset ?? '') : '';
              return (
                <label key={f.zone} className="field small">{label}
                  <select disabled={disabled} value={v} onChange={(e) => setField(f.zone, !e.target.value ? null
                    : kind === 'playlist' ? { type: 'playlist', items: [{ asset: e.target.value }] } : { type: 'media', asset: e.target.value })}>
                    <option value="">{t('gallery.placeholderImage')}</option>
                    {images.map((m) => <option key={m.id} value={m.current!.sha256}>{m.name}</option>)}
                  </select>
                </label>
              );
            }
            return null;
          })}
          {fields.length > 0 && <span className="small muted">{t('tedit.embedFieldsHint')}</span>}
        </div>
      )}
    </>
  );
}
